import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Flex from '../../../components/Flex';
import { Select } from '../../../components/Select';
import { LIBRARY_LINKS } from '../../../constants';
import { Button, IconDownload as DownloadIcon, IconInfoCircle as InfoIcon } from '../../../core';
import { useDownloadFile } from '../../../hooks/useDownloadFile';
import { ReactComponent as BuilderIcon } from '../../../images/icons/builder-overview.svg';
import { ReactComponent as EsriIcon } from '../../../images/icons/esri.svg';
import { ReactComponent as ExcelIcon } from '../../../images/icons/excel.svg';
import { ReactComponent as FigmaIcon } from '../../../images/icons/figma.svg';
import { ReactComponent as PowerIcon } from '../../../images/icons/power-bi.svg';

type LibrariesSelectProps = {
  className?: string;
  onSelect?: (value: string) => void;
  defaultValue?: string;
  optionsToExclude?: string[];
};

const DEFAULT_LIBRARIES_SELECT_OPTIONS_TO_EXCLUDE: NonNullable<
  LibrariesSelectProps['optionsToExclude']
> = [];

const LibrariesSelect = ({
  className = '',
  onSelect,
  defaultValue,
  optionsToExclude = DEFAULT_LIBRARIES_SELECT_OPTIONS_TO_EXCLUDE,
}: LibrariesSelectProps) => {
  const navigate = useNavigate();
  const downloadFile = useDownloadFile();

  const selectOptions = [
    {
      value: 'builder',
      plain: true,
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 4px', outline: 'none' }}
          align="center"
          justify="flex-start"
        >
          <BuilderIcon />
          <span style={{ marginLeft: '8px' }}>Download Resources</span>
        </Flex>
      ),
    },
    {
      value: 'powerBI',
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 4px' }}
          align="center"
          justify="space-between"
          onPointerDown={() => downloadFile('PBI Library.pbix', LIBRARY_LINKS.pbi_lib_path)}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PowerIcon />
            <span style={{ marginLeft: '8px' }}>Power BI Library</span>
          </div>
          <Flex className="info-download-icon" gap="8px" align="center">
            <Button
              onPointerDown={(e) => {
                e.stopPropagation();
                navigate('/dev-resources/powerbikit');
              }}
              iconOnly
              variant="ghost"
              size="XS"
              icon={<InfoIcon />}
              aria-label="About the Power BI Library"
            />
            <Button
              iconOnly
              size="XS"
              icon={<DownloadIcon />}
              aria-label="Download the Power BI Library"
            />
          </Flex>
        </Flex>
      ),
    },
    {
      value: 'excel',
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 4px' }}
          align="center"
          justify="space-between"
          onPointerDown={() => downloadFile('Excel Library.xlsx', LIBRARY_LINKS.excel_lib_path)}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ExcelIcon />
            <span style={{ marginLeft: '8px' }}>Excel Library</span>
          </div>
          <Flex className="info-download-icon" gap="8px" align="center">
            <Button
              onPointerDown={(e) => {
                e.stopPropagation();
                navigate('/dev-resources/excel-library');
              }}
              iconOnly
              variant="ghost"
              size="XS"
              icon={<InfoIcon />}
              aria-label="About the Excel Library"
            />
            <Button
              iconOnly
              size="XS"
              icon={<DownloadIcon />}
              aria-label="Download the Excel Library"
            />
          </Flex>
        </Flex>
      ),
    },
    {
      value: 'esri',
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 4px' }}
          align="center"
          justify="space-between"
          onPointerDown={() => downloadFile('Esri Library.ppkx', LIBRARY_LINKS.esri_lib_path)}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EsriIcon />
            <span style={{ marginLeft: '8px' }}>Esri Library</span>
          </div>
          <Flex className="info-download-icon" gap="8px" align="center">
            <Button
              onPointerDown={(e) => {
                e.stopPropagation();
                navigate('/dev-resources/esrikit');
              }}
              iconOnly
              variant="ghost"
              size="XS"
              icon={<InfoIcon />}
              aria-label="About the Esri Library"
            />
            <Button
              iconOnly
              size="XS"
              icon={<DownloadIcon />}
              aria-label="Download the Esri Library"
            />
          </Flex>
        </Flex>
      ),
    },
    {
      value: 'figma',
      label: (
        <Flex
          style={{ width: '100%', padding: '8px 4px' }}
          align="center"
          justify="space-between"
          onPointerDown={() => downloadFile('Figma Library.fig', LIBRARY_LINKS.figma_lib_path)}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FigmaIcon />
            <span style={{ marginLeft: '8px' }}>Figma Library</span>
          </div>
          <Flex className="info-download-icon" gap="8px" align="center">
            <Button
              onPointerDown={(e) => {
                e.stopPropagation();
                navigate('/dev-resources/figma-library');
              }}
              iconOnly
              variant="ghost"
              size="XS"
              icon={<InfoIcon />}
              aria-label="About the Figma Library"
            />
            <Button
              iconOnly
              size="XS"
              icon={<DownloadIcon />}
              aria-label="Download the Figma Library"
            />
          </Flex>
        </Flex>
      ),
    },
  ];

  const options = selectOptions.filter((option) => !optionsToExclude?.includes(option.value));

  return (
    <Select
      paddingZero
      defaultValue={defaultValue}
      setStaticDefaultOption
      onSelect={onSelect}
      className={className}
      data={options}
    />
  );
};

const StyledSelect = styled(LibrariesSelect)`
  .select-selected-option {
    .info-download-icon {
      margin-right: 16px;
        button {
          &:first-child {
            background-color: transparent;
          }
        }
      }
    }

    .select-selected-option__icon {
      margin-right: 8px;
      display: flex;
      align-items: center;
    }
  }

  [role='option'] {
    .select-options-item__icon {
      margin-right: 8px;
      display: flex;
      align-items: center;
    }

    .select-options-item__label {
      display: flex;
      justify-content: space-between;
      width: 100%;
      padding:0px;

      // svg {
      //   > path {
      //     fill: ${({ theme }) => theme.neutralTextWeak};
      //   }
      // }
    }
`;

export default StyledSelect;
